/**
 * Cliente API para los endpoints de configuración de columnas.
 * Spec: docs/architecture/COLUMN_CONFIG_PROPOSAL.md (Slice 1+2)
 *
 * Todas las llamadas requieren un token Firebase con rol admin/superadmin.
 * El token se inyecta vía interceptor en `src/utils/api.ts`.
 */
import api from '../utils/api';
import type { FormatType } from '../utils/columnFormatters';
import type { ColumnTemplatesMap } from './columnTemplates';
import type { ExportAttrs } from './columnExportAttrs';

// ----- Tipos -----

export interface CoreEntry {
  enabled?: boolean;
  displayName?: string;
  description?: string;
  order?: number;
  // Merge silencioso toma↔catalog: si está seteado, la columna existente
  // se renderiza con MergedTomaCatalogCell que combina row[columnKey] con
  // row[mergeWithCatalog]. Whitelist: catalog_brand | catalog_category |
  // subcategory | segmento | contenido | udm. null/undefined → render normal.
  mergeWithCatalog?: string | null;
  // Inline edit: si true Y el catalog field efectivo está en EDITABLE_WHITELIST,
  // la celda se renderiza con input inline (estilo edit SKU). Default false.
  editable?: boolean;
  // PT-#70: atributos DEFAULT de exportación a Excel (title/format/widthChars) de esta
  // columna. Es el nivel "columnConfig.export" de la cadena de resolución; los templates
  // pueden overridearlo. Separado del `format?: FormatType` (render on-screen). Ver
  // src/services/columnExportAttrs.ts. NativeEntry/TagEntry heredan este campo.
  export?: ExportAttrs;
}

// Whitelist de campos del catalog editables vía PATCH. Debe matchear con
// ALLOWED_FIELDS de server/utils/catalogPatch.js (backend enforcement real).
export const EDITABLE_WHITELIST = [
  'catalog_brand',
  'catalog_category',
  'subcategory',
  'segmento',
  'contenido',
  'udm',
] as const;
export type EditableField = (typeof EDITABLE_WHITELIST)[number];

// Helper: dado una entry (Core/Native), devuelve el catalog field editable
// efectivo. Prioridad: mergeWithCatalog > sourceField (si en whitelist).
export function resolveEditableField(entry: CoreEntry | NativeEntry): EditableField | null {
  const merge = (entry as CoreEntry).mergeWithCatalog;
  if (merge && (EDITABLE_WHITELIST as readonly string[]).includes(merge)) {
    return merge as EditableField;
  }
  const src = (entry as NativeEntry).sourceField;
  if (src && (EDITABLE_WHITELIST as readonly string[]).includes(src)) {
    return src as EditableField;
  }
  return null;
}

export interface NativeEntry extends CoreEntry {
  sourceField?: string;
  format?: FormatType;
  decimals?: number;
  thousandsSeparator?: boolean;
  currencySymbol?: string;
  pendingReview?: boolean;
}

export interface TagEntry extends CoreEntry {
  format?: FormatType;
  decimals?: number;
  thousandsSeparator?: boolean;
  currencySymbol?: string;
  pendingReview?: boolean;
}

// ----- Filter layout config -----

export interface FilterField {
  key: string;      // 'brand' | 'site' | 'category' | 'subcategory' | 'segment'
  enabled: boolean;
  widthPct: number; // 25, 33, 50, etc.
}

export interface FilterRow {
  fields: FilterField[];
}

export interface FilterLayoutConfig {
  rows: FilterRow[];
}

export const DEFAULT_FILTER_LAYOUT: FilterLayoutConfig = {
  rows: [
    {
      fields: [
        { key: 'brand', enabled: true, widthPct: 25 },
        { key: 'site', enabled: true, widthPct: 25 },
      ],
    },
    {
      fields: [
        { key: 'category', enabled: true, widthPct: 25 },
        { key: 'subcategory', enabled: true, widthPct: 25 },
        { key: 'segment', enabled: true, widthPct: 25 },
      ],
    },
  ],
};

export interface ColumnConfigDoc {
  schemaVersion?: number;
  lastSchemaScan?: any;
  lastTagsScan?: any;
  core: Record<string, CoreEntry>;
  native: Record<string, NativeEntry>;
  tags: Record<string, TagEntry>;
  filterLayout?: FilterLayoutConfig;
  // Templates de columnas por pantalla (PUBLICACIONES / DATA EXPORT). Embebido
  // acá igual que filterLayout — el backend lo persiste vía el `...doc` spread
  // de columnConfigManager.save. Ver src/services/columnTemplates.ts (PT-#67).
  templates?: ColumnTemplatesMap;
}

export interface NativeCandidate {
  id: string;
  sourceField: string;
  sqlType: string;
  typeHint: FormatType;
  sample: unknown;
  coverage: number;
  alreadyConfigured: boolean;
  reserved?: boolean;
}

export interface TagCandidate {
  tagKey: string;
  rowCount: number;
  sampleValue: unknown;
  alreadyConfigured: boolean;
}

export interface ScanNativeResponse {
  clientId: string;
  lastTomaScanned: string | null;
  scannedAt: string;
  candidates: NativeCandidate[];
}

export interface DiscoverableTagsResponse {
  clientId: string;
  tags: TagCandidate[];
}

export interface GetColumnConfigResponse {
  clientId: string;
  columnConfig: ColumnConfigDoc;
  exists: boolean;
}

// ----- Helpers -----

const adminHeaders = (clientId: string) => ({
  'x-client-id': clientId,
});

// ----- Endpoints -----

export async function getColumnConfig(clientId: string): Promise<GetColumnConfigResponse> {
  const res = await api.get<GetColumnConfigResponse>(
    `/api/clients/${clientId}/column-config`,
    { headers: adminHeaders(clientId) }
  );
  return res.data;
}

export async function saveColumnConfig(
  clientId: string,
  doc: Partial<ColumnConfigDoc>
): Promise<{ clientId: string; columnConfig: ColumnConfigDoc }> {
  const res = await api.put(
    `/api/clients/${clientId}/column-config`,
    doc,
    { headers: adminHeaders(clientId) }
  );
  return res.data;
}

export async function patchColumnConfig(
  clientId: string,
  partial: Partial<ColumnConfigDoc>
): Promise<{ clientId: string; columnConfig: ColumnConfigDoc }> {
  const res = await api.patch(
    `/api/clients/${clientId}/column-config`,
    partial,
    { headers: adminHeaders(clientId) }
  );
  return res.data;
}

export async function scanNativeColumns(clientId: string): Promise<ScanNativeResponse> {
  const res = await api.post<ScanNativeResponse>(
    `/api/clients/${clientId}/column-config/scan-native`,
    {},
    { headers: adminHeaders(clientId) }
  );
  return res.data;
}

export async function getDiscoverableTags(clientId: string): Promise<DiscoverableTagsResponse> {
  const res = await api.get<DiscoverableTagsResponse>(
    `/api/clients/${clientId}/column-config/discoverable-tags`,
    { headers: adminHeaders(clientId) }
  );
  return res.data;
}

export interface SampleValuesResponse {
  clientId: string;
  field: string;
  type: 'native' | 'tags';
  hasToma: boolean;
  latestToma?: string;
  totalDistinct: number;
  values: { value: unknown; count: number }[];
}

export async function getSampleValues(
  clientId: string,
  field: string,
  type: 'native' | 'tags',
  limit = 20
): Promise<SampleValuesResponse> {
  const res = await api.get<SampleValuesResponse>(
    `/api/clients/${clientId}/column-config/sample-values`,
    { headers: adminHeaders(clientId), params: { field, type, limit } }
  );
  return res.data;
}
